import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Shield, Database, Users, Lock, Eye, Phone, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contactInfo } from '../mock';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="max-w-4xl mx-auto">
          <Button 
            onClick={() => navigate('/book')}
            variant="outline"
            className="mb-8 flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Booking</span>
          </Button>

          <Card className="shadow-xl border-0 bg-white">
            <CardContent className="p-8 sm:p-12">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold text-lg">
                    AIS
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
                </div>
                <p className="text-slate-600">AIS Express Same-Day Delivery Services</p>
                <p className="text-sm text-slate-500 mt-2">Last Updated: 24/10/2025</p>
              </div>

              <div className="prose prose-slate max-w-none">
                
                <p className="text-slate-700 leading-relaxed mb-8">
                  This Privacy Policy explains how AIS Express ("we" or "us") collects, uses, and protects your personal information when you use our delivery services.
                </p>

                {/* 1. Information We Collect */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Database className="h-5 w-5 mr-2 text-emerald-600" />
                    1. Information We Collect
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    We may collect the following information:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>Name</li>
                    <li>Phone number</li>
                    <li>Email address (if provided)</li>
                    <li>Pickup and delivery addresses</li>
                    <li>Invoice / payment-related information</li>
                  </ul>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                    <p className="text-blue-800">
                      <strong>Note:</strong> We do not store payment card details, as we invoice customers separately.
                    </p>
                  </div>
                </section>

                {/* 2. How We Use Your Information */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-emerald-600" />
                    2. How We Use Your Information
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    We use your information to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>Arrange collection and delivery services</li>
                    <li>Contact you regarding your order</li>
                    <li>Send invoices and payment reminders</li>
                    <li>Comply with legal or regulatory requirements</li>
                  </ul>
                  <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mt-4">
                    <p className="text-emerald-800 font-medium">
                      We do not sell or share your information with any third parties for marketing.
                    </p>
                  </div>
                </section>

                {/* 3. Data Storage */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Lock className="h-5 w-5 mr-2 text-emerald-600" />
                    3. Data Storage
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    Your information may be stored securely in:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>Delivery systems</li>
                    <li>Email or communication records</li>
                    <li>Invoice/accounting systems</li>
                  </ul>
                  <p className="text-slate-700 leading-relaxed mt-4">
                    We take reasonable steps to protect data from unauthorized access or misuse.
                  </p>
                </section>

                {/* 4. Sharing Information */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-emerald-600" />
                    4. Sharing Information
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    We may share information only:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>If required by law or government authority</li>
                    <li>With delivery partners when necessary to complete your service</li>
                  </ul>
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4">
                    <p className="text-amber-800 font-medium">
                      We do not share your details with advertisers or unrelated businesses.
                    </p>
                  </div>
                </section>

                {/* 5. Your Rights */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Eye className="h-5 w-5 mr-2 text-emerald-600" />
                    5. Your Rights
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    You can request to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    <li>View the personal data we hold about you</li>
                    <li>Correct any inaccurate information</li>
                    <li>Ask us to delete your data (where legally permitted)</li>
                  </ul>
                  <p className="text-slate-700 leading-relaxed mt-4">
                    To request this, contact us at the details below.
                  </p>
                </section>

                {/* 6. Contact */}
                <section className="mb-8">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
                    <Phone className="h-5 w-5 mr-2 text-emerald-600" />
                    6. Contact
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    If you have questions about this Privacy Policy, contact:
                  </p>
                  <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Mail className="h-4 w-4 text-emerald-600" />
                      <span className="text-slate-700">Email: <a href={`mailto:${contactInfo.email}`} className="text-emerald-600 hover:text-emerald-700 font-medium">{contactInfo.email}</a></span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-emerald-600" />
                      <span className="text-slate-700">Phone: <a href={`tel:${contactInfo.phone}`} className="text-emerald-600 hover:text-emerald-700 font-medium">{contactInfo.phone}</a></span>
                    </div>
                  </div>
                </section>

              </div>

              {/* Agreement Footer */}
              <div className="mt-12 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-600 text-center">
                  By using AIS Express services, you acknowledge that you have read and understood this Privacy Policy.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
